import { toDateKey } from './dateKey';

export interface Holiday {
  dateKey: string;
  name: string;
  /** Short (<=6 char) label that fits on a calendar day cell. */
  abbr: string;
}

/** The nth (1-indexed) occurrence of `weekday` (0=Sun..6=Sat) in a month. */
function nthWeekdayOfMonth(year: number, month: number, weekday: number, n: number): Date {
  const first = new Date(year, month, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month, 1 + offset + (n - 1) * 7);
}

/** The last occurrence of `weekday` (0=Sun..6=Sat) in a month. */
function lastWeekdayOfMonth(year: number, month: number, weekday: number): Date {
  const last = new Date(year, month + 1, 0);
  const offset = (last.getDay() - weekday + 7) % 7;
  return new Date(year, month, last.getDate() - offset);
}

/**
 * The 11 US federal holidays, computed for any year rather than hardcoded
 * per date so this doesn't need yearly maintenance. Uses each holiday's
 * actual calendar date, not the Friday/Monday-shifted "observed" date
 * federal employees get off — for travel planning, the real date (e.g.
 * July 4th itself) is what matters, not the observance shift.
 */
export function getFederalHolidays(year: number): Holiday[] {
  return [
    { date: new Date(year, 0, 1), name: "New Year's Day", abbr: 'NYD' },
    { date: nthWeekdayOfMonth(year, 0, 1, 3), name: 'Martin Luther King Jr. Day', abbr: 'MLK' },
    { date: nthWeekdayOfMonth(year, 1, 1, 3), name: "Presidents' Day", abbr: 'PRES' },
    { date: lastWeekdayOfMonth(year, 4, 1), name: 'Memorial Day', abbr: 'MEM' },
    { date: new Date(year, 5, 19), name: 'Juneteenth', abbr: 'JUNE' },
    { date: new Date(year, 6, 4), name: 'Independence Day', abbr: 'JUL 4' },
    { date: nthWeekdayOfMonth(year, 8, 1, 1), name: 'Labor Day', abbr: 'LABOR' },
    { date: nthWeekdayOfMonth(year, 9, 1, 2), name: 'Columbus Day', abbr: 'COLUM' },
    { date: new Date(year, 10, 11), name: 'Veterans Day', abbr: 'VETS' },
    { date: nthWeekdayOfMonth(year, 10, 4, 4), name: 'Thanksgiving Day', abbr: 'T-DAY' },
    { date: new Date(year, 11, 25), name: 'Christmas Day', abbr: 'XMAS' },
  ].map(({ date, name, abbr }) => ({ dateKey: toDateKey(date), name, abbr }));
}

export function federalHoliday(dateKey: string): Holiday | null {
  const year = Number(dateKey.slice(0, 4));
  return getFederalHolidays(year).find((h) => h.dateKey === dateKey) ?? null;
}
